import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_BOOKING_REPORT, SEARCH_LOCATION, GET_PAYMENT_METHODS, GET_ROUTE_TYPES, GET_ROUTES_FOR_REPORTS } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18n from 'i18next'
import i18next from 'i18next'

// Constants
const AGE_GROUPS = [
    { code: 'ADL', label: 'Adult' },
    { code: 'CHD', label: 'Child' },
    { code: 'INF', label: 'Infant' },
    { code: 'SNR', label: 'Senior Citizen' },
]

const BOOKING_PLATFORMS = [
    { code: 'WEB', label: 'Website' },
    { code: 'MOBILE', label: 'Mobile App' },
]

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]

const BOOKING_STATUS = [
    { code: 'CXL', label: 'Cancelled only' },
    { code: 'NOS', label: 'No show' },
    { code: 'CMP', label: 'Completed' }, // Added completed status
]

const BOOKING_TYPES = [
    { code: 'STD', description: { en: 'Standard Booking', ar: 'الحجز القياسي' }, default: true },
    { code: 'BDP', description: { en: 'Standard Booking(Day Pass)', ar: 'الحجز القياسي (تصريح يومي)' } },
    { code: 'DYP', description: { en: 'Day pass', ar: 'تصريح يومي' } },
]

// Add date type options
const DATE_TYPE_OPTIONS = [
    { code: 'TRAVEL_DATE', label: 'Travel Date' },
    { code: 'PAYMENT_DATE', label: 'Payment Date' },
]

// Add customer add-on options
const ADD_ON_TYPES = [
    { code: 'SHUTTLE_SERVICE', label: 'Shuttle Service'},
    { code: 'MEAL', label: 'Meal'},
    { code: 'LUGGAGE', label: 'Luggage'},
]

const INITIAL_FILTER_STATE = {
    date_type: 'TRAVEL_DATE', // new field
    travel_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    return_date: '',
    passanger_age_group_code: '',
    location_range: {
        start_location_id: '',
        end_location_id: '',
    },
    booking_type_code: 'STD',
    booking_platform: '',
    booking_status: '',
    payment_method: '',
    route_ids: [],
    trip_ids: [],
    route_type: '',
    add_on_type: '',
}

export default function BookingReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [prevPage, setPrevPage] = useState(null)
    const [sortField, setSortField] = useState('travelDate')
    const [sortDirection, setSortDirection] = useState('desc')
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [locations, setLocations] = useState([])
    const [paymentMethods, setPaymentMethods] = useState([])
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [routeTypes, setRouteTypes] = useState([])
    const [filterApplied, setFilterApplied] = useState(false)
    const [appliedBookingTypeCode, setAppliedBookingTypeCode] = useState(INITIAL_FILTER_STATE.booking_type_code)
    const [totalValues, setTotalValues] = useState(null)
    const [returnDate, setReturnDate] = useState('');
    const [isValidationError, setIsValidationError] = useState(false);
    const [searchDate, setSearchDate] = useState('today'); // or moment().format('YYYY-MM-DD')
    const filterObjRef = useRef(filterObj)
    const appliedBookingTypeCodeRef = useRef(appliedBookingTypeCode)
    useEffect(() => {
        filterObjRef.current = filterObj
    }, [filterObj])

    useEffect(() => {
        appliedBookingTypeCodeRef.current = appliedBookingTypeCode
    }, [appliedBookingTypeCode])

    // Cleanup on unmount to prevent state updates on unmounted component
    useEffect(() => {
        return () => {
            // Cleanup function - component is unmounting
            setData([])
            setFilterApplied(false)
        }
    }, [])

    // GraphQL query
    const [getBookingReport, { loading: reportLoading }] = useLazyQuery(GET_BOOKING_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.bookingReport.success && data.bookingReport.code === '200') {
                setData(data.bookingReport.booking_report.map(formatBookingData))
                setTotalRows(data.bookingReport.total_count)
                setTotalValues(data.bookingReport.total)
            } else {
                handleError(data.bookingReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const [searchLocation] = useLazyQuery(SEARCH_LOCATION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchLocation.success && data.searchLocation.code === '200') {
                setLocations(data.searchLocation.locations)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getPaymentMethods] = useLazyQuery(GET_PAYMENT_METHODS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.paymentMethods) {
                setPaymentMethods(data.paymentMethods)
            }
        },
        onError: error => handleError(error.message),
    })

    // Get route types query
    const [getRouteTypes] = useLazyQuery(GET_ROUTE_TYPES, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.routeType) {
                setRouteTypes(data.routeType)
            }
        },
        onError: error => handleError(error.message),
    })

    // Get routes query
    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => handleError(error.message),
    })

    // Helper Functions
    const formatBookingData = booking => ({
        serial_number: booking.serial_number,
        bookingRef: booking.booking_code,
        travel_date: booking.travel_date,
        return_date: booking.return_date,
        booking_date: booking.booking_date,
        platform: booking.booking_platform,
        locationDetail: booking?.location_detail ? booking?.location_detail[i18next?.language || 'en'] : '-',
        bookingType: booking?.booking_type ? booking?.booking_type[i18next?.language || 'en'] : '-',
        userType: booking.user_type,
        passengers: {
            infant: booking.infant_count,
            child: booking.child_count,
            adult: booking.adult_count,
            senior: booking.senior_count,
        },
        vip_count: booking.vip_count,
        std_count: booking.std_count,
        wallet_amount_used: booking.wallet_amount_used,
        refund_amount: booking.refund_amount,
        refund_date: booking.refund_date,
        discounts: booking.discount_amount,
        net_revenue: booking.net_amount_paid,
        gross_revenue: booking.gross_ticket_price,
        booking_status: booking?.booking_status ? booking?.booking_status[i18next?.language || 'en'] : '-',
        receipt_number: booking.receipt_number,
        payment_mode: booking.payment_mode,
        daypass_code: booking.daypass_code,
        bk_count_by_daypass: booking.bk_count_by_daypass,
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch booking report'))
        setShowErrDialog(true)
    }

    // Modify getBookingReportForExport to remove CSV generation
    const [getBookingReportForExport] = useLazyQuery(GET_BOOKING_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    const fetchBatch = async (offset, limit) => {
        try {
            // Use the latest filterObj value from the ref
            const currentFilterObj = filterObjRef.current

            const result = await getBookingReportForExport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    bookingFilter: {
                        date_range_type: currentFilterObj.date_type,
                        travel_date: {
                            start_date:
                                currentFilterObj.travel_date.start_date ||
                                moment().subtract(1, 'months').format('YYYY-MM-DD'),
                            end_date: currentFilterObj.travel_date.end_date || moment().format('YYYY-MM-DD'),
                        },
                        passanger_age_group_code: currentFilterObj.passanger_age_group_code || null,
                        location_range: {
                            start_location_id: currentFilterObj.location_range.start_location_id || null,
                            end_location_id: currentFilterObj.location_range.end_location_id || null,
                        },
                        booking_type_code: currentFilterObj.booking_type_code || null,
                        booking_platform: currentFilterObj.booking_platform || null,
                        booking_status: currentFilterObj.booking_status || null,
                        payment_method: currentFilterObj.payment_method || null,
                        route_ids: currentFilterObj.route_ids?.length > 0 ? currentFilterObj.route_ids : null,
                        trip_ids: currentFilterObj.trip_ids?.length > 0 ? currentFilterObj.trip_ids : null,
                        route_type: currentFilterObj.route_type || null,
                        add_on_type: currentFilterObj.add_on_type || null
                    },
                },
            })
            if (result.data?.bookingReport?.success && result.data.bookingReport.code === '200') {
                return {
                    data: result.data.bookingReport.booking_report,
                    total: result.data.bookingReport.total_count,
                    summaryTotals: result.data.bookingReport.total,
                }
            } else {
                throw new Error(result.data?.bookingReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    const isDYPBooking = bookingTypeCode => bookingTypeCode === 'DYP'

    // Create a total row for the data table
    const getTotalRow = () => {
        if (!totalValues) return null

        return {
            serial_number: 'Total',
            bookingRef: '',
            travel_date: '', // Empty string instead of invalid date
            booking_date: '',
            platform: '',
            locationDetail: '',
            bookingType: '',
            userType: '',
            passengers: {
                infant: totalValues.infant_count || 0,
                child: totalValues.child_count || 0,
                adult: totalValues.adult_count || 0,
                senior: totalValues.senior_count || 0,
            },
            vip_count: totalValues.vip_count || 0,
            std_count: totalValues.std_count || 0,
            wallet_amount_used: totalValues.wallet_amount_used || 0,
            refund_amount: totalValues.refund_amount || 0,
            refund_date: '',
            discounts: totalValues.discount_amount || 0,
            net_revenue: totalValues.net_amount_paid || 0,
            gross_revenue: totalValues.gross_ticket_price || 0,
            booking_status: '',
            receipt_number: '',
            payment_mode: '',
            daypass_code: '',
            bk_count_by_daypass: totalValues.std_count || 0,
            add_on_type: '',
            isTotal: true,
        }
    }

    // Modified data with total row
    const tableData = useMemo(() => {
        if (!data || data.length === 0 || !totalValues) return data
        return [...data, getTotalRow()]
    }, [data, totalValues])

    const columns = useMemo(() => {
        const baseColumns = [
            {
                name: t('S.No'),
                selector: row => row.serial_number,
                width: '80px',
                sortable: false,
                center: false,
                cell: row => <div className={row.isTotal ? 'font-bold' : ''}>{row.serial_number}</div>,
            },
            {
                name: t('Travel Date'),
                selector: row => row.travel_date,
                sortable: false,
                cell: row => (row.isTotal ? '' : moment(row.travel_date).format('DD MMM YYYY')),
                width: '120px',
                center: false,
            },
            {
                name: t('Return Date'),
                selector: row => row.return_date,
                sortable: true,
                cell: row => row.isTotal ? '' : (
                    row.return_date ? 
                    moment(row.return_date).format('DD MMM YYYY') : 
                    '-'
                ),
                width: '130px',
               
            },
            {
                name: t('Booking Date'),
                selector: row => row.booking_date,
                sortable: false,
                cell: row =>
                    row.isTotal ? '' : row.booking_date ? moment(row.booking_date).format('DD MMM YYYY') : '-',
                width: '120px',
                center: false,
            },
            {
                name: t('Booking Code'),
                selector: row => row.bookingRef,
                sortable: false,
                width: '130px',
                cell: row => <div className="whitespace-nowrap">{row.isTotal ? '' : row.bookingRef}</div>,
                center: false,
            },
            {
                name: t('Booking Platform'),
                selector: row => row.platform,
                sortable: false,
                cell: row => (row.isTotal ? '' : <Badge color="blue">{row.platform}</Badge>),
                width: '140px',
                center: false,
            },
        ]

        const locationColumns = [
            {
                name: t('Location Details'),
                selector: row => row.locationDetail,
                sortable: false,
                wrap: true,
                width: '200px',
                cell: row => <div className="">{row.isTotal ? '' : row.locationDetail}</div>,
            },
        ]

        const passpangerCountColumn = [
            {
                name: t('Infant'),
                selector: row => row.passengers.infant,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row.passengers.infant}</span>,
                width: '100px',
                center: false,
            },
            {
                name: t('Child'),
                selector: row => row.passengers.child,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row.passengers.child}</span>,
                width: '80px',
                center: false,
            },
            {
                name: t('Adult'),
                selector: row => row.passengers.adult,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row.passengers.adult}</span>,
                width: '80px',
                center: false,
            },
            {
                name: t('Senior'),
                selector: row => row.passengers.senior,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row.passengers.senior}</span>,
                width: '80px',
                center: false,
            },
            {
                name: t('VIP Count'),
                selector: row => row.vip_count,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row?.vip_count || 0}</span>,
                width: '100px',
                center: false,
            },
            {
                name: t('STD Count'),
                selector: row => row.std_count,
                sortable: false,
                cell: row => <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row?.std_count || 0}</span>,
                width: '100px',
                center: false,
            },
            {
                name: t('Total Passengers'),
                sortable: false,
                cell: row => {
                    const total =
                        row.passengers.infant + row.passengers.child + row.passengers.adult + row.passengers.senior
                    return <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{total}</span>
                },
                width: '140px',
                center: false,
            },
        ]

        const ticketCountColumn = [
            {
                name: t('Ticket Count'),
                sortable: false,
                width: '120px',
                cell: row => (
                    <span className={row.isTotal ? 'font-bold' : 'font-medium'}>{row.bk_count_by_daypass || 0}</span>
                ),
            },
        ]

        const dayPassCodeColumn = [
            {
                name: t('Day Pass Code'),
                sortable: false,
                width: '140px',
                cell: row => <div className="">{row.isTotal ? '' : row.daypass_code || '-'}</div>,
            },
        ]

        const standardColumns = [
            {
                name: `${t('Discounts')} (BHD)`,
                selector: row => row.discounts,
                sortable: false,
                cell: row => (
                    <div className="">
                        <span className={row.isTotal ? 'font-bold' : 'font-medium'}>
                            {row.discounts?.toFixed(3) || '0.000'}
                        </span>
                    </div>
                ),
                width: '120px',
                center: false,
            },
            {
                name: `${t('Wallet Amount Used')} (BHD)`,
                selector: row => row?.wallet_amount_used,
                sortable: false,
                cell: row => (
                    <div className="">
                        <span className={row.isTotal ? 'font-bold' : 'font-medium'}>
                            {row.wallet_amount_used?.toFixed(3) || '0.000'}
                        </span>
                    </div>
                ),
                width: '140px',
                center: false,
            },
            {
                name: `${t('Net Amount Paid')} (BHD)`,
                selector: row => row.net_revenue,
                sortable: false,
                cell: row => (
                    <div className="">
                        <span className={row.isTotal ? 'font-bold' : 'font-medium'}>
                            {row.net_revenue?.toFixed(3) || '0.000'}
                        </span>
                    </div>
                ),
                width: '140px',
                center: false,
            },
            {
                name: `${t('Gross Ticket Price')} (BHD)`,
                selector: row => row.gross_revenue,
                sortable: false,
                cell: row => (
                    <div className="">
                        <span className={row.isTotal ? 'font-bold' : 'font-medium'}>
                            {row.gross_revenue?.toFixed(3) || '0.000'}
                        </span>
                    </div>
                ),
                width: '140px',
                center: false,
            },
        ]

        const commonColumns = [
            {
                name: t('Booking Type'),
                selector: row => row.bookingType,
                sortable: false,
                width: '160px',
                cell: row => <div className="text-start">{row.isTotal ? '' : row.bookingType}</div>,
            },
            {
                name: t('User Type'),
                selector: row => row.userType,
                sortable: false,
                cell: row => (row.isTotal ? '' : <Badge color="amber">{row.userType}</Badge>),
                width: '120px',
                center: false,
            },
            {
                name: t('Booking Status'),
                selector: row => row.booking_status,
                sortable: false,
                cell: row => (
                    <div className="">
                        {row.isTotal ? (
                            ''
                        ) : (
                            <Badge
                                color={
                                    row.booking_status === 'Cancelled'
                                        ? 'rose'
                                        : row.booking_status === 'No Show'
                                        ? 'orange'
                                        : row.booking_status === 'Trip Cancelled'
                                        ? 'rose'
                                        : row.booking_status === 'Completed'
                                        ? 'blue'
                                        : row.booking_status === 'Confirmed'
                                        ? 'emerald'
                                        : 'gray'
                                }
                            >
                                {row.booking_status}
                            </Badge>
                        )}
                    </div>
                ),
                width: '140px',
                center: false,
            },
        ]

        const receiptsColumns = [
            {
                name: t('Receipt Number'),
                selector: row => row.receipt_number,
                sortable: false,
                cell: row => <div className="">{row.isTotal ? '' : row.receipt_number || '-'}</div>,
                width: '130px',
                center: false,
            },
            {
                name: t('Payment Mode'),
                selector: row => row.payment_mode,
                sortable: false,
                cell: row => <div className="">{row.isTotal ? '' : row.payment_mode || '-'}</div>,
                width: '130px',
                center: false,
            },
            {
                name: `${t('Refund Amount')} (BHD)`,
                selector: row => row.refund_amount,
                sortable: false,
                cell: row => (
                    <div className="">
                        <span className={row.isTotal ? 'font-bold' : 'font-medium'}>
                            {row.refund_amount?.toFixed(3) || '0.000'}
                        </span>
                    </div>
                ),
                width: '140px',
                center: false,
            },
            {
                name: t('Refund Date'),
                selector: row => row.refund_date,
                sortable: false,
                cell: row => (
                    <div className="">
                        {row.isTotal ? '' : row.refund_date ? moment(row.refund_date).format('DD MMM YYYY') : '-'}
                    </div>
                ),
                width: '120px',
                center: false,
            },
        ]

        const addOnColumns = [
            {
                name: t('meals'),
                selector: row => row.meal,
                sortable: false,
                cell: row => <div className="">{row.isTotal ? '' : row.receipt_number || '-'}</div>,
                width: '130px',
                center: false,

            },
            {
                name: t('extra_luggage'),
                selector: row => row.meal,
                sortable: false,
                cell: row => <div className="">{row.isTotal ? '' : row.receipt_number || '-'}</div>,
                width: '130px',
                center: false,

            },
            {
                name: t('shuttle_service'),
                selector: row => row.meal,
                sortable: false,
                cell: row => <div className="">{row.isTotal ? '' : row.receipt_number || '-'}</div>,
                width: '130px',
                center: false,


            },
        ]

        if (appliedBookingTypeCode === 'DYP') {
            return [
                ...baseColumns,
                ...ticketCountColumn,
                ...passpangerCountColumn,
                ...standardColumns,
                ...commonColumns,
            ]
        } else if (appliedBookingTypeCode === 'BDP') {
            return [
                ...baseColumns,
                ...locationColumns,
                ...passpangerCountColumn,
                ...dayPassCodeColumn,
                ...commonColumns,
            ]
        } else {
            return [
                ...baseColumns,
                ...locationColumns,
                ...passpangerCountColumn,
                ...standardColumns,
                ...receiptsColumns,
                ...commonColumns,
            ]
        }
    }, [t, appliedBookingTypeCode, totalValues])

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                color: '#111827',
                fontSize: '0.875rem',
                fontWeight: 600,
                paddingLeft: '16px',
                paddingRight: '16px',
                justifyContent: 'flex-start', // Center header text
            },
        },
        cells: {
            style: {
                paddingLeft: '16px',
                paddingRight: '16px',
                fontSize: '0.875rem',
            },
        },
        rows: {
            style: {
                borderBottom: '1px solid #e5e7eb',
                '&:hover': {
                    backgroundColor: '#f9fafb',
                },
            },
        },
    }

    // Add conditional row styles for the total row
    const conditionalRowStyles = [
        {
            when: row => row.isTotal,
            style: {
                backgroundColor: '#d7e9f0',
                fontWeight: 'bold',
            },
        },
    ]

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)

            // Make sure we're using the currently applied filters
            // If filter hasn't been applied yet, apply it first
            if (!filterApplied) {
                handleFilter()
                // Wait a bit for the filter to be applied
                await new Promise(resolve => setTimeout(resolve, 500))
            }

            const initialBatch = await fetchBatch(0, 100)
            const totalRecords = initialBatch.total
            const summaryTotals = initialBatch.summaryTotals

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            while (allData.length < totalRecords) {
                const batchData = await fetchBatch(offset, 100)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            const formattedData = allData.map(formatBookingData)

            // Define common base columns
            const baseColumns = {
                [t('S.No')]: row => row.serial_number,
                [t('Travel Date')]: row => moment(row.travel_date).format('DD MMM YYYY'),
                [t('Booking Date')]: row => (row.booking_date ? moment(row.booking_date).format('DD MMM YYYY') : '-'),
                [t('Booking Code')]: row => row.bookingRef,
                [t('Booking Platform')]: row => row.platform,
            }

            // Define passenger columns
            const passengerColumns = {
                [t('Infant')]: row => row.passengers.infant,
                [t('Child')]: row => row.passengers.child,
                [t('Adult')]: row => row.passengers.adult,
                [t('Senior')]: row => row.passengers.senior,
                [t('VIP Count')]: row => row.vip_count || 0,
                [t('STD Count')]: row => row.std_count || 0,
                [t('Total Passengers')]: row =>
                    row.passengers.infant + row.passengers.child + row.passengers.adult + row.passengers.senior,
            }

            // Define common end columns
            const commonColumns = {
                [t('Booking Type')]: row => row.bookingType,
                [t('User Type')]: row => row.userType,
                [t('Booking Status')]: row => row.booking_status,
            }

            // Create the total row for CSV
            const totalRow = {
                [t('S.No')]: 'TOTAL',
                [t('Travel Date')]: '',
                [t('Booking Date')]: '',
                [t('Booking Code')]: '',
                [t('Booking Platform')]: '',
                [t('Location Details')]: '',
                [t('Infant')]: summaryTotals.infant_count || 0,
                [t('Child')]: summaryTotals.child_count || 0,
                [t('Adult')]: summaryTotals.adult_count || 0,
                [t('Senior')]: summaryTotals.senior_count || 0,
                [t('VIP Count')]: summaryTotals.vip_count || 0,
                [t('STD Count')]: summaryTotals.std_count || 0,
                [t('Total Passengers')]:
                    (summaryTotals.infant_count || 0) +
                    (summaryTotals.child_count || 0) +
                    (summaryTotals.adult_count || 0) +
                    (summaryTotals.senior_count || 0),
                [t('Booking Type')]: '',
                [t('User Type')]: '',
                [t('Booking Status')]: '',
                [t('Discounts')]: summaryTotals.discount_amount?.toFixed(3) || '0.000',
                [t('Wallet Amount Used')]: summaryTotals.wallet_amount_used?.toFixed(3) || '0.000',
                [t('Net Amount Paid')]: summaryTotals.net_amount_paid?.toFixed(3) || '0.000',
                [t('Gross Ticket Price')]: summaryTotals.gross_ticket_price?.toFixed(3) || '0.000',
                [t('Receipt Number')]: '',
                [t('Payment Mode')]: '',
                [t('Refund Amount')]: summaryTotals.refund_amount?.toFixed(3) || '0.000',
                [t('Refund Date')]: '',
                [t('Ticket Count')]: summaryTotals.std_count || 0,
                [t('Day Pass Code')]: '',
            }

            // Use the latest booking type code from the ref
            const currentBookingTypeCode = appliedBookingTypeCodeRef.current

            // Select columns based on booking type
            let selectedColumns = {}
            if (currentBookingTypeCode === 'DYP') {
                selectedColumns = {
                    ...baseColumns,
                    [t('Ticket Count')]: row => row.bk_count_by_daypass || 0,
                    ...passengerColumns,
                    [t('Discounts')]: row => row.discounts?.toFixed(3) || '0.000',
                    [t('Wallet Amount Used')]: row => row.wallet_amount_used?.toFixed(3) || '0.000',
                    [t('Net Amount Paid')]: row => row.net_revenue?.toFixed(3) || '0.000',
                    [t('Gross Ticket Price')]: row => row.gross_revenue?.toFixed(3) || '0.000',
                    ...commonColumns,
                }
            } else if (currentBookingTypeCode === 'BDP') {
                selectedColumns = {
                    ...baseColumns,
                    [t('Location Details')]: row => row.locationDetail,
                    ...passengerColumns,
                    [t('Day Pass Code')]: row => row.daypass_code || '-',
                    ...commonColumns,
                }
            } else {
                selectedColumns = {
                    ...baseColumns,
                    [t('Location Details')]: row => row.locationDetail,
                    ...passengerColumns,
                    [t('Discounts')]: row => row.discounts?.toFixed(3) || '0.000',
                    [t('Wallet Amount Used')]: row => row.wallet_amount_used?.toFixed(3) || '0.000',
                    [t('Net Amount Paid')]: row => row.net_revenue?.toFixed(3) || '0.000',
                    [t('Gross Ticket Price')]: row => row.gross_revenue?.toFixed(3) || '0.000',
                    [t('Receipt Number')]: row => row.receipt_number || '-',
                    [t('Payment Mode')]: row => row.payment_mode || '-',
                    [t('Refund Amount')]: row => row.refund_amount?.toFixed(3) || '0.000',
                    [t('Refund Date')]: row => (row.refund_date ? moment(row.refund_date).format('DD MMM YYYY') : '-'),
                    ...commonColumns,
                }
            }

            // Convert data using selected columns
            const exportData = formattedData.map(row => {
                const rowData = {}
                Object.entries(selectedColumns).forEach(([header, getter]) => {
                    rowData[header] = getter(row)
                })
                return rowData
            })

            // Add the total row to the export data
            const headers = Object.keys(selectedColumns)
            let totalRowForCSV = {}
            headers.forEach(header => {
                totalRowForCSV[header] = totalRow[header] !== undefined ? totalRow[header] : ''
            })
            exportData.push(totalRowForCSV)

            // Create CSV content
            const csvContent = [
                headers.join(','),
                ...exportData.map(row => headers.map(header => row[header]).join(',')),
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `booking-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const fetchBookingReport = (page, limit = perPage) => {
        const offset = (page - 1) * limit

        // Use the latest filterObj value from the ref
        const currentFilterObj = filterObjRef.current

        const bookingFilter = {
            date_range_type: currentFilterObj.date_type,
            travel_date: {
                start_date:
                    currentFilterObj.travel_date.start_date || moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: currentFilterObj.travel_date.end_date || moment().format('YYYY-MM-DD'),
            },
            passanger_age_group_code: currentFilterObj.passanger_age_group_code || null,
            location_range: {
                start_location_id: currentFilterObj.location_range.start_location_id || null,
                end_location_id: currentFilterObj.location_range.end_location_id || null,
            },
            booking_type_code: currentFilterObj.booking_type_code || null,
            booking_platform: currentFilterObj.booking_platform || null,
            booking_status: currentFilterObj.booking_status || null,
            route_ids: currentFilterObj.route_ids?.length > 0 ? currentFilterObj.route_ids : null,
            trip_ids: currentFilterObj.trip_ids?.length > 0 ? currentFilterObj.trip_ids : null,
            route_type: currentFilterObj.route_type || null,
            add_on_type: currentFilterObj.add_on_type || null,
        }

        // Add payment_method for all types except BDP
        if (currentFilterObj.booking_type_code !== 'BDP') {
            bookingFilter.payment_method = currentFilterObj.payment_method || null
        }

        getBookingReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                bookingFilter,
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchBookingReport(currentPage, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleFilter = () => {
        setCurrentPage(1)
        setPrevPage(null)
        setFilterApplied(true)
        setAppliedBookingTypeCode(filterObj.booking_type_code) // Set the applied booking type code here
        fetchBookingReport(1)
    }

    const handleClear = () => {
        setFilterObj({
            date_type: 'TRAVEL_DATE',
            travel_date: {
                start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
            passanger_age_group_code: '',
            location_range: {
                start_location_id: '',
                end_location_id: '',
            },
            booking_type_code: 'STD',
            booking_platform: '',
            booking_status: '',
            payment_method: '',
            route_ids: [],
            trip_ids: [],
            route_type: '',
            add_on_type: ''
        })

        setCurrentPage(1)
        setPrevPage(null)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear the existing data
        setAppliedBookingTypeCode('STD') // Reset applied booking type code
        setTrips([]) // Clear trips
    }

    useEffect(() => {
        document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr'
    }, [i18n.language])

    const isLoading = reportLoading

    const handleLocationChange = (field, value) => {
        setFilterObj(prev => ({
            ...prev,
            location_range: {
                ...prev.location_range,
                [field]: value || null,
            },
        }))
    }

    const fetchLocations = (searchString = '') => {
        searchLocation({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                searchString,
            },
        })
    }

    useEffect(() => {
        fetchLocations()
        getPaymentMethods()
        getRouteTypes()
        fetchRoutes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Fetch routes
    const fetchRoutes = () => {
        getRoutes({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                statusCode: 'ACT',
            },
        })
    }

    // When routes are selected, collect trips from all selected routes
    useEffect(() => {
        if (filterObj.route_ids?.length > 0) {
            const selectedRoutes = routes.filter(r => filterObj.route_ids.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter((trip, index, self) =>
                index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [filterObj.route_ids, routes])

    const locationInputsJSX = (
        <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Location Range')}</label>
            <div className="mt-0 flex gap-2">
                <div className="flex-1">
                    <Select
                        value={filterObj.location_range.start_location_id || ''}
                        onChange={e => handleLocationChange('start_location_id', e.target.value)}
                    >
                        <option value="">{t('All')}</option>
                        {locations.map(location => (
                            <option key={location.id} value={location.id}>
                                {`${location.name.en + '/' + location.name.ar}`}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="flex items-center">
                    <span className="text-gray-500">→</span>
                </div>
                <div className="flex-1">
                    <Select
                        value={filterObj.location_range.end_location_id || ''}
                        onChange={e => handleLocationChange('end_location_id', e.target.value)}
                    >
                        <option value="">{t('All')}</option>
                        {locations.map(location => (
                            <option key={location.id} value={location.id}>
                                {`${location.name.en + '/' + location.name.ar}`}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>
        </div>
    )

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <DocumentTextIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Bookings Report')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Overview of all user bookings')}
                            </p>
                        </div>
                    </h1>
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Date Type')}</label>
                            <Select
                                value={filterObj.date_type}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        date_type: e.target.value,
                                    })
                                }
                            >
                                {DATE_TYPE_OPTIONS.map(opt => (
                                    <option key={opt.code} value={opt.code}>
                                        {t(opt.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 ">
                                {filterObj.date_type === 'TRAVEL_DATE'
                                    ? t('Travel Date Range')
                                    : t('Payment Date Range')}
                            </label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        travel_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Booking Type')}</label>
                            <Select
                                value={filterObj.booking_type_code || 'STD'} // Changed from '' to 'STD'
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        booking_type_code: e.target.value || 'STD', // Changed from null to 'STD'
                                    })
                                }
                            >
                                <option value="STD">
                                    {i18n.language === 'ar' ? 'الحجز القياسي' : 'Standard Booking'}
                                </option>
                                {BOOKING_TYPES.filter(type => type.code !== 'STD').map(type => (
                                    <option key={type.code} value={type.code}>
                                        {i18n.language === 'ar' && type.description.ar
                                            ? type.description.ar
                                            : type.description.en}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="sm:col-span-1">
  <label className="block text-sm font-medium text-gray-700 ">
    {t('Return Date')}
  </label>
  <Flatpickr
    value={returnDate}
    placeholder={t('Return Date')}
    onChange={date => {
      setReturnDate(
        date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '/') : ''
      );
      setIsValidationError(false);
    }}
    options={{
      altInput: true,
      altFormat: 'd/m/Y',
      dateFormat: 'Y/m/d',
      minDate: searchDate || 'today',
      maxDate: new Date().fp_incr(30),
      locale: {
        firstDayOfWeek: 1,
        weekdays: {
          shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          longhand: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
        },
        months: {
          shorthand: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
          longhand: ['January','February','March','April','May','June','July','August','September','October','November','December'],
        },
      },
    }}
    className="mt-1 p-[0.25rem] pr-1.5 pl-[0.6rem] block w-full rounded-md border-gray-300 shadow-sm placeholder:[font-size:14px] placeholder:[color:#404040]"

  />
</div>


                        {/* Add payment method filter conditionally */}
                        {filterObj.booking_type_code !== 'BDP' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 pb-1">
                                    {t('Payment Method')}
                                </label>
                                <Select
                                    value={filterObj.payment_method || ''}
                                    onChange={e =>
                                        setFilterObj({
                                            ...filterObj,
                                            payment_method: e.target.value || null,
                                        })
                                    }
                                >
                                    <option value="">{t('All')}</option>
                                    {paymentMethods.map(method => (
                                        <option key={method.code} value={method.code}>
                                            {i18n.language === 'ar' ? method.description.ar : method.description.en}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Age Group')}</label>
                            <Select
                                value={filterObj.passanger_age_group_code || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        passanger_age_group_code: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {AGE_GROUPS.map(group => (
                                    <option key={group.code} value={group.code}>
                                        {t(group.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Platform')}</label>
                            <Select
                                value={filterObj.booking_platform || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        booking_platform: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {BOOKING_PLATFORMS.map(platform => (
                                    <option key={platform.code} value={platform.code}>
                                        {t(platform.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">
                                {t('Booking Status')}
                            </label>
                            <Select
                                value={filterObj.booking_status || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        booking_status: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {BOOKING_STATUS.map(status => (
                                    <option key={status.code} value={status.code}>
                                        {t(status.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Route Type')}</label>
                            <Select
                                value={filterObj.route_type || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        route_type: e.target.value || null,
                                        route_ids: [], // Clear routes when route type changes
                                        trip_ids: [], // Clear trips when route type changes
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {routeTypes.map(type => (
                                    <option key={type.code} value={type.code}>
                                        {i18n.language === 'ar' ? type.description.ar : type.description.en}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Route')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.route_ids || []}
                                onChange={(values) =>
                                    setFilterObj({
                                        ...filterObj,
                                        route_ids: values,
                                        trip_ids: [], // Clear trips when routes change
                                    })
                                }
                                options={routes
                                    .filter(route => {
                                        if (!filterObj.route_type) return true
                                        return route.master_route?.route_type?.code === filterObj.route_type
                                    })
                                    .map(route => ({
                                        value: route.route_id,
                                        label: i18n.language === 'ar' ? route.route_name.ar : route.route_name.en,
                                    }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Trip')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.trip_ids || []}
                                onChange={(values) =>
                                    setFilterObj({
                                        ...filterObj,
                                        trip_ids: values,
                                    })
                                }
                                options={trips.map(trip => ({
                                    value: trip.trip_id,
                                    label: i18n.language === 'ar' ? trip.trip_name.ar : trip.trip_name.en,
                                }))}
                                disabled={filterObj.route_ids?.length === 0}
                            />
                        </div>

                        {/* Replace the separate location inputs with a grouped layout */}
                        {filterObj.booking_type_code !== 'DYP' && locationInputsJSX}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Add-On')}</label>
                            <Select
                                value={filterObj.add_on_type || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        add_on_type: e.target.value,
                                    })
                                }
                            >
                                <option value="">{t('All')}</option>
                                {ADD_ON_TYPES.map(type => (
                                    <option key={type.code} value={type.code}>
                                        {t(type.label)}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        {/* Add filter buttons to grid */}
                        <div className="sm:col-span-1 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider className="my-4 mb-6" />
            {isLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Booking Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        sortServer
                        onSort={(column, direction) => {
                            setSortField(column.sortField)
                            setSortDirection(direction)
                        }}
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        paginationPerPage={perPage}
                        noDataComponent={
                            <div className=" py-24 bg-gray-50 mt-0 rounded-lg w-full text-center">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                                <div className="mt-6"></div>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
